import React from "react";
import {
  IonContent,
  IonHeader,
  IonToolbar,
  IonModal,
  IonButton,
  IonTitle,
  IonButtons,
} from "@ionic/react";
import { FormattedMessage, useIntl } from "react-intl";

import MessageContent from "./message-content";
import { Form } from "react-bootstrap";
import { Message } from "../../interfaces";
import PlaceholderReplacer from "../outbound/templates/placeholder";
import "./media-modal.css";

// Styles
import styles from "./message-actions-modal.module.css";

// API
import ChatQualityApi from "../../api/chat-quality";

// Utils
import { extractContext } from "../../utils/messages";
import { ms } from "date-fns/locale";

const ActionsModal: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const intl = useIntl();
  const { isOpen, setIsOpen, action, message, messages } = props;
  const [loading, setLoading] = React.useState(false);
  const [response, setResponse] = React.useState<string | null>(null);

  const [flagForm, setFlagForm] = React.useState<{
    expectedResponse: string;
    userMessage: string;
    assistantResponse: string;
    userMessages: Message[];
    assistantMessages: Message[];
  }>({
    expectedResponse: "",
    userMessage: "",
    assistantResponse: "",
    userMessages: [],
    assistantMessages: [],
  });

  React.useEffect(() => {
    if (["flag", "debug"].includes(action) && message) {
      const result = extractContext(messages, message.id);
      if (result) {
        const { concurrentBotMessages, previousUserMessages } = result;
        setFlagForm({
          expectedResponse: message.flag?.comments || "",
          userMessages: previousUserMessages,
          assistantMessages: concurrentBotMessages,
          userMessage: previousUserMessages
            .map((msg) => msg.content)
            .join("\n\n"),
          assistantResponse: concurrentBotMessages
            .map((msg) => msg.content)
            .join("\n\n"),
        });
      }
    }
  }, [action, message]);

  function onWillDismiss() {
    setIsOpen(false);
  }

  const handleFlagSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResponse(null);
    setLoading(true);
    const { data, err } = await ChatQualityApi.flagMessage({
      messageId: message?.id,
      ...flagForm,
    });
    setLoading(false);
    if (data) {
      onWillDismiss();
    }
  };

  const handleDebugSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResponse(null);
    setLoading(true);
    const { data, err } = await ChatQualityApi.debugMessage({
      messageId: message?.id,
      ...flagForm,
    });
    setLoading(false);
    if (data) {
      setResponse(data.analysis);
    }
  };

  const renderContent = () => {
    if (!message) {
      return null;
    }

    switch (action) {
      case "flag":
        return (
          <Form onSubmit={handleFlagSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>
                <FormattedMessage
                  id="chats.userMessage"
                  defaultMessage="User message"
                />
              </Form.Label>

              <div className="chat-detail-list">
                {flagForm.userMessages.map((msg) => (
                  <div className="chat-item mb-2" key={msg.id}>
                    {" "}
                    <MessageContent key={msg.id} message={msg} type="debug" />
                  </div>
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                <FormattedMessage id="chats.assistantResponse" />
              </Form.Label>

              <div className="chat-detail-list">
                {flagForm.assistantMessages.map((msg) => (
                  <div className="right" key={msg.id}>
                    <div className="chat-item mb-2">
                      <MessageContent key={msg.id} message={msg} type="debug" />
                    </div>
                  </div>
                ))}
              </div>
            </Form.Group>
            <Form.Group>
              <Form.Label>
                <FormattedMessage id="chats.expectedResponse" />
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder={intl.formatMessage({
                  id: "chats.expectedResponsePlaceholder",
                })}
                value={flagForm.expectedResponse}
                onChange={(e) =>
                  setFlagForm({ ...flagForm, expectedResponse: e.target.value })
                }
                required
              />
            </Form.Group>
            <div className={styles.actionsContainer}>
              <button
                className="btn btn-visito-primary"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FormattedMessage id="common.loading" />
                    ...
                  </>
                ) : (
                  <FormattedMessage id="chats.updateAnswer" />
                )}
              </button>
            </div>
          </Form>
        );

      case "debug":
        return (
          <Form onSubmit={handleDebugSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>
                <FormattedMessage
                  id="chats.userMessage"
                  defaultMessage="User message"
                />
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                readOnly
                value={flagForm.userMessage}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                <FormattedMessage id="chats.assistantResponse" />
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                readOnly
                value={flagForm.assistantResponse}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                <FormattedMessage id="chats.expectedResponse" />
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder={intl.formatMessage({
                  id: "chats.expectedResponsePlaceholder",
                })}
                value={flagForm.expectedResponse}
                onChange={(e) =>
                  setFlagForm({ ...flagForm, expectedResponse: e.target.value })
                }
                required
              />
            </Form.Group>
            {response && (
              <div className="chat-detail-list">
                <div className="right">
                  <div className="chat-item mt-2">
                    <PlaceholderReplacer
                      template={response}
                      placeholders={[]}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className={styles.actionsContainer}>
              <button
                className="btn btn-visito-primary"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <FormattedMessage id="common.loading" />
                    ...
                  </>
                ) : (
                  <FormattedMessage id="chats.debugAnswer" />
                )}
              </button>
            </div>
          </Form>
        );

      default:
        return (
          <p>
            <FormattedMessage
              id="common.unknownAction"
              defaultMessage="Unknown action."
            />
          </p>
        );
    }
  };

  return (
    <IonModal isOpen={isOpen} onWillDismiss={() => onWillDismiss()}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            <FormattedMessage id={`chats.${action}`} />
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setIsOpen(false)}>
              <FormattedMessage id="common.cancel" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className={styles.actionModalContainer}>{renderContent()}</div>
      </IonContent>
    </IonModal>
  );
};

interface ILayoutProps {
  isOpen: boolean;
  setIsOpen: Function;
  action: string;
  message: Message | null;
  messages: Message[];
}

export default ActionsModal;
